/**
 * Chart abstraction — shared types.
 *
 * `<Chart>` is a library-agnostic wrapper that dispatches to one of four
 * underlying chart libraries (Recharts / Visx / D3 / ECharts) based on the
 * user's active selection (Dev Console slicer, stored in localStorage).
 *
 * Each chart type defines its own props interface; ChartProps is the
 * discriminated union dispatched by the Chart component. The "view" each
 * library renders should be visually equivalent given the same data and theme.
 */

export type ChartLib = 'recharts' | 'visx' | 'd3' | 'echarts';

export const CHART_LIBS: readonly ChartLib[] = ['recharts', 'visx', 'd3', 'echarts'] as const;
export const DEFAULT_CHART_LIB: ChartLib = 'recharts';

export type ChartType =
  | 'bar'
  | 'stacked-bar'
  | 'grouped-bar'
  | 'combo'
  | 'pie'
  | 'donut'
  | 'line'
  | 'area'
  | 'geo'
  | 'sparkline-table';

/** Standard datum shape — label + numeric value. Most charts accept this. */
export interface CategoricalDatum {
  label: string;
  value: number;
  /** Optional explicit color override (skips palette lookup). */
  color?: string;
  /** Arbitrary additional fields available to tooltip + breakdown logic. */
  [key: string]: unknown;
}

/** Multi-series datum for line/area/stacked-bar — one x value, multiple y series. */
export interface SeriesDatum {
  x: string | number;
  /** Series key → numeric value. */
  [seriesKey: string]: string | number;
}

/** Geo datum for map charts — one point per node/site/endpoint. */
export interface GeoDatum {
  id: string;
  label: string;
  latitude: number;
  longitude: number;
  value?: number;
  status?: 'online' | 'offline' | 'warning' | 'unknown';
  [key: string]: unknown;
}

/** Sparkline-table row — table row with an inline sparkline column. */
export interface SparklineRow {
  id: string;
  label: string;
  /** Most recent value for the headline column. */
  current: number;
  /** Time-series values driving the sparkline. */
  series: number[];
  [key: string]: unknown;
}

// ── Per-type prop interfaces ───────────────────────────────────────────────

export interface BarChartProps {
  type: 'bar';
  data: CategoricalDatum[];
  /** Optional axis labels. */
  xLabel?: string;
  yLabel?: string;
  /** Layout direction. Default 'vertical' (bars rise). */
  orientation?: 'vertical' | 'horizontal';
}

export interface StackedBarChartProps {
  type: 'stacked-bar';
  data: SeriesDatum[];
  /** Field on each datum used as the x axis category. */
  xKey: string;
  /** Series keys to stack. */
  seriesKeys: string[];
  xLabel?: string;
  yLabel?: string;
  /** Layout direction. Default 'vertical' (bars rise). */
  orientation?: 'vertical' | 'horizontal';
}

export interface GroupedBarChartProps {
  type: 'grouped-bar';
  data: SeriesDatum[];
  /** Field on each datum used as the category axis. */
  xKey: string;
  /** Series keys, each rendered as a side-by-side bar within the category. */
  seriesKeys: string[];
  xLabel?: string;
  yLabel?: string;
  /** Layout direction. Default 'vertical' (bars rise). */
  orientation?: 'vertical' | 'horizontal';
}

/**
 * Combo chart — one bar series + one line series on a shared x axis.
 * Used for "new per period + cumulative total" widgets (e.g., Deployments by
 * Quarter, Total Endpoints by Quarter). Both series share the same domain;
 * the line gets a secondary y-axis if `dualAxis: true`.
 */
export interface ComboChartProps {
  type: 'combo';
  data: SeriesDatum[];
  xKey: string;
  /** Field key rendered as bars. */
  barKey: string;
  /** Field key rendered as a line on top. */
  lineKey: string;
  /** Optional display label for the bar series in tooltips/legend. */
  barLabel?: string;
  /** Optional display label for the line series in tooltips/legend. */
  lineLabel?: string;
  /** When true, give the line series its own right-side y-axis. */
  dualAxis?: boolean;
  xLabel?: string;
  yLabel?: string;
}

export interface PieChartProps {
  type: 'pie' | 'donut';
  data: CategoricalDatum[];
  /** Hide labels for slices below this percentage (0–1). Default 0.08. */
  minLabelPercent?: number;
  /** Show interactive hover highlight on active slice. Default true. */
  enableHover?: boolean;
}

export interface LineChartProps {
  type: 'line' | 'area';
  data: SeriesDatum[];
  xKey: string;
  seriesKeys: string[];
  xLabel?: string;
  yLabel?: string;
}

export interface GeoChartProps {
  type: 'geo';
  data: GeoDatum[];
  /** Optional projection — defaults to 'mercator'. */
  projection?: 'mercator' | 'naturalEarth' | 'equalEarth';
  /** Country/region GeoJSON URL — for libs that need it. */
  worldDataUrl?: string;
}

export interface SparklineTableProps {
  type: 'sparkline-table';
  data: SparklineRow[];
  /** Column header for the label column. */
  labelHeader?: string;
  /** Column header for the headline numeric column. */
  currentHeader?: string;
  /** Column header for the sparkline column. */
  sparklineHeader?: string;
}

// ── Discriminated union ────────────────────────────────────────────────────

export type ChartProps =
  | BarChartProps
  | StackedBarChartProps
  | GroupedBarChartProps
  | ComboChartProps
  | PieChartProps
  | LineChartProps
  | GeoChartProps
  | SparklineTableProps;

/** Common layout props shared by all chart types. */
export interface CommonChartProps {
  /** Container height in pixels. Default 280 (matches existing widget convention). */
  height?: number;
  /** Empty-state message. Default 'No data'. */
  emptyMessage?: string;
  /** Test id for E2E selectors. */
  'data-testid'?: string;
  /** Library override — when set, bypasses the user's slicer selection. */
  libOverride?: ChartLib;
  /** Accessible label for the chart. */
  ariaLabel?: string;
}

export type FullChartProps = ChartProps & CommonChartProps;
